import { AnimatePresence, motion, type Variants } from 'motion/react'
import { useEffect, useRef, useState } from 'react'
import { useLocation, useOutlet } from 'react-router-dom'
import { useIsDesktop } from '@/shared/hooks/use-is-desktop'

const TAB_ORDER = ['/plan', '/discovery', '/profile']

function getTabIndex(pathname: string) {
  const idx = TAB_ORDER.findIndex((p) => pathname.startsWith(p))
  return idx === -1 ? 0 : idx
}

const variants: Variants = {
  enter: (dir: number) => ({
    x: `${dir * 100}%`,
  }),
  center: {
    x: 0,
  },
  exit: (dir: number) => ({
    x: `${-dir * 100}%`,
  }),
}

export function AnimatedOutlet() {
  const location = useLocation()
  const outlet = useOutlet()
  const prevPathRef = useRef(location.pathname)
  const isDesktop = useIsDesktop()
  // Picked per visit: swapping the wrapper on a resize remounts the open page.
  const [visit, setVisit] = useState({
    pathname: location.pathname,
    isDesktop,
  })
  if (visit.pathname !== location.pathname) {
    setVisit({ pathname: location.pathname, isDesktop })
  }

  const currentIndex = getTabIndex(location.pathname)
  const prevIndex = getTabIndex(prevPathRef.current)
  const direction =
    currentIndex > prevIndex ? 1 : currentIndex < prevIndex ? -1 : 1

  useEffect(() => {
    prevPathRef.current = location.pathname
  }, [location.pathname])

  if (visit.isDesktop) {
    return <div className="absolute inset-0">{outlet}</div>
  }

  return (
    <AnimatePresence mode="sync" initial={false} custom={direction}>
      <motion.div
        key={location.pathname}
        custom={direction}
        variants={variants}
        initial="enter"
        animate="center"
        exit="exit"
        transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
        className="absolute inset-0"
      >
        {outlet}
      </motion.div>
    </AnimatePresence>
  )
}
